// 매출 목록 표의 '담당강사' 필터.
//
// 기준은 **판매 시점의 담당강사**(coachId / coachName)다. 표의 '담당강사' 열이 보여주는 값과
// 같은 필드를 써야 필터 결과와 열 표기가 어긋나지 않는다. 인계 이후의 현재 강사(currentCoachId)는
// 쓰지 않는다.
//
// 그룹 수강권 행(source === 'group')은 서버가 coachName 을 빈 문자열로 내려주고 coachId 만 있어,
// 특정 강사를 고르면 이름 없는 행이 섞여 나온다. 그룹 수업은 강사 필터의 대상이 아니므로 옵션에서도
// 제외하고, 강사를 고른 순간 결과에서도 뺀다. '전체'일 때는 그대로 보인다.
//
// 옵션 키는 수강권 필터(buildMembershipTemplateOptions)와 같은 `id:` / `name:` 규칙이다. 옛 응답이나
// 레거시 행은 coachId 가 비어 있을 수 있어 이름으로도 묶는다.

export const COACH_FILTER_ALL = 'all';

export type CoachFilterOption = { code: string; value: string };

type CoachFilterRowLike = {
    coachId?: string | null;
    coachName?: string | null;
    source?: 'pt' | 'group' | string | null;
};

const isGroupRow = (row: CoachFilterRowLike) => row.source === 'group';

export const buildCoachFilterOptions = (rows?: CoachFilterRowLike[] | null): CoachFilterOption[] => {
    const optionMap = new Map<string, CoachFilterOption>();

    (rows ?? []).forEach((row) => {
        if (isGroupRow(row)) return;

        const coachId = row.coachId?.trim();
        const coachName = row.coachName?.trim();

        if (coachId) {
            const key = `id:${coachId}`;
            if (!optionMap.has(key)) {
                optionMap.set(key, { code: key, value: coachName || coachId });
            }
            return;
        }

        if (!coachName) return;

        const key = `name:${coachName}`;
        if (!optionMap.has(key)) {
            optionMap.set(key, { code: key, value: coachName });
        }
    });

    return Array.from(optionMap.values());
};

export const isMatchingCoachFilter = (row: CoachFilterRowLike, selectedOption: string): boolean => {
    if (selectedOption === COACH_FILTER_ALL) return true;
    if (isGroupRow(row)) return false;
    if (selectedOption.startsWith('id:')) {
        return (row.coachId ?? '') === selectedOption.slice(3);
    }
    if (selectedOption.startsWith('name:')) {
        return (row.coachName ?? '').trim() === selectedOption.slice(5);
    }
    return (row.coachId ?? '') === selectedOption;
};

/**
 * 월·판매자가 바뀌어 새 데이터가 오면 고른 강사가 옵션에 없을 수 있다. 그대로 두면 0건이 보이므로
 * '전체'로 되돌린다.
 */
export const resolveCoachFilterSelection = (selectedOption: string, options: CoachFilterOption[]): string => {
    if (selectedOption === COACH_FILTER_ALL) return COACH_FILTER_ALL;
    return options.some((option) => option.code === selectedOption) ? selectedOption : COACH_FILTER_ALL;
};
